import React, { useEffect, useState } from "react";
import { IBucketFilterParams, IBucketListItem, IBucketPageRequestParams } from "api/entities/bucket";
import { IIdName } from "api/entities/id-name";
import { IImportValidationStatus, IImportValidationSuccessStatus } from "api/entities/import-bzs";
import { IPage } from "api/entities/page";
import { bucketService } from "api/services/bucket-service";
import { importBzsConfigurationV2Service } from "api/services/import-bzs-configuration-v2-service";
import { shardClusterService } from "api/services/shard-cluster-service";
import { catchException } from "../../redux/actions/common";
import { getDefaultRequestParams, getEmptyPage } from "../../redux/reducers/entity";
import { useApi } from "../../utils/hooks/use-api";
import { useFilters } from "../../utils/hooks/use-filter";
import { useNavigate } from "react-router-dom";
import { routeUtils } from "../../utils/route-utils";
import Button from "../shared-ui/button";
import RefreshButton from "../shared-ui/button/refresh-button";
import IOption from "../shared-ui/common/option";
import { FiltersData } from "../shared-ui/filters-data/filters-data/filters-data";
import PageTitle from "../shared-ui/pageTitle";
import { ImportBzsResultDialog } from "../import-bzs-configuration/dialogs/import-bzs-result-dialog/import-bzs-result-dialog";
import { defaultFilters, filterNames, IShardClusterAssociatedFilters, searchBarStoragePoolFilterTypeMap, ShardClusterAssociatedFilters } from "../common/sections/filters/shard-cluster-associated-filters";
import { ImportButton } from "../import-bzs-configuration/import-button/import-button";
import { BucketTable } from "./bucket-table/bucket-table";
import styles from "./buckets.module.scss";
import { DeleteBlockedConfirmation } from "./dialogs/delete-blocked-confirmation/delete-blocked-confirmation";
import { DeleteConfirmation } from "./dialogs/delete-confirmation/delete-confirmation";
import BucketPanel from "./panels/bucket-panel/bucket-panel";
import { IFile } from "api/entities/file";
import { jobService } from "api/services/job-service";

export default function Buckets() {
    const [bucketApi, isBucketLoading] = useApi();
    const [storagePoolApi] = useApi();
    const [jobsApi, isJobsApiLoading] = useApi();
    const [bucketPage, setBucketPage] = useState<IPage<IBucketListItem>>(getEmptyPage());
    const [requestParams, setRequestParams] = useState<IBucketPageRequestParams>({...getDefaultRequestParams(), deleted: false});

    const [bzsConfigurationFile, setBzsConfigurationFile] = useState<IFile>(null);
    const [isBzsImportResultDialogOpen, setIsBzsImportResultDialogOpen] = useState<boolean>(false);

    const [storagePoolOptions, setStoragePoolOptions] = useState<IOption<string>[]>([]);

    const [importResult, setImportResult] = useState<IImportValidationSuccessStatus | IImportValidationStatus[]>(null);

    const navigate = useNavigate();

    const [filters, setFilters, updateQuery] = useFilters(
        defaultFilters,
        requestData,
        searchBarStoragePoolFilterTypeMap,
        filtersToQueryParams,
        queryParamsToFilters
    );

    function filtersToQueryParams(filters: IShardClusterAssociatedFilters): IBucketFilterParams {
        return {
            q: filters.searchText,
            pool: filters.shardClusterFilter?.value
        };
    }

    function queryParamsToFilters(queryParams: Partial<IBucketFilterParams>): IShardClusterAssociatedFilters {
        return {
            searchText: queryParams.q,
            shardClusterFilter: queryParams.pool ?
                { value: queryParams.pool }
                : null
        };
    }

    function requestData(filters: IShardClusterAssociatedFilters) {
        fetchData({
            ...filtersToQueryParams(filters),
            offset: 0,
        });
    }

    function fetchData(requestParamsUpdate: Partial<IBucketPageRequestParams>) {
        const updatedRequestParams = {...requestParams, ...requestParamsUpdate};
        setRequestParams(updatedRequestParams);
        bucketApi(bucketService.getPage(updatedRequestParams)).then(setBucketPage);
    }

    function fetchStoragePoolOptionList() {
        storagePoolApi(shardClusterService.getPage({ fields: ['id', 'name'] }))
            .then((page: IPage<IIdName<string>>) => {
                return page.data.map(storagePool => ({ value: storagePool.id, label: storagePool.name }));
            })
            .then(setStoragePoolOptions);
    }

    function handleAddBucketFormPanelOpen() {
        navigate(routeUtils.getRouteToNewBucket());
    }

    function handleAddBucketFormPanelClose() {
        navigate(routeUtils.getRouteToBucketList());
    }

    function handleDeleteBucket(bucketId: string) {
        jobsApi(jobService.deleteBzs(bucketId)).then(() => fetchData({}));
    }

    useEffect(() => {
        fetchStoragePoolOptionList();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!!storagePoolOptions && storagePoolOptions.length > 0 &&
            !!filters.shardClusterFilter && !filters.shardClusterFilter.label) {
            let shardClusterFilter = storagePoolOptions.find(x => x.value === filters.shardClusterFilter.value);
            setFilters({ ...filters, shardClusterFilter });
        }
        // eslint-disable-next-line
    }, [storagePoolOptions, filters.shardClusterFilter]);

    useEffect(() => {
        if (bzsConfigurationFile && !!bzsConfigurationFile.fileData) {
            importBzsConfigurationV2Service.upload(bzsConfigurationFile.fileData)
                .then(result => {
                    setImportResult(result);
                    setIsBzsImportResultDialogOpen(true);
                })
                .catch(() => {
                    setImportResult([]);
                    setIsBzsImportResultDialogOpen(true);
                    catchException();
                });
        }
        // eslint-disable-next-line
    }, [bzsConfigurationFile]);

    return (
        <>
            <div className={styles.header}>
                <PageTitle title={`Buckets`}>
                    <RefreshButton onClick={() => fetchData(requestParams)}/>
                </PageTitle>

                <div className={styles["action-buttons"]}>
                    <ImportButton
                        testId='import-bzs-button'
                        colors="primary"
                        file={bzsConfigurationFile}
                        onChange={setBzsConfigurationFile}
                    />
                    <Button
                        testId='bucket-create-button'
                        colors="primary"
                        onClick={handleAddBucketFormPanelOpen}
                    >
                        Create
                    </Button>
                </div>
            </div>


            <ShardClusterAssociatedFilters
                filters={filters}
                onFiltersChange={updateQuery}
                storagePoolOptions={storagePoolOptions}
            />
            <FiltersData
                filterNames={filterNames}
                defaultFilters={defaultFilters}
                filters={filters}
                onFiltersChange={updateQuery}
            />

            <BucketTable
                isLoading={isBucketLoading || isJobsApiLoading}
                page={bucketPage}
                requestParams={requestParams}
                setRequestParams={fetchData}
                hasManageColumn={true}
                isRowClickable={true}
            />

            <BucketPanel
                embedded={false}
                fetchBuckets={() => fetchData({})}
                onClose={handleAddBucketFormPanelClose}
            />

            <ImportBzsResultDialog
                isOpen={isBzsImportResultDialogOpen}
                importResult={importResult}
                onClose={() => {
                    setIsBzsImportResultDialogOpen(false);

                    if (!!importResult) {
                        fetchData(requestParams);
                    }

                    setImportResult(null);
                }}
            />

            <DeleteConfirmation
                onDelete={handleDeleteBucket}
            />

            <DeleteBlockedConfirmation />
        </>
    );

}
